import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  createSupplier,
  updateSupplier,
  fetchSupplierbyId,
} from "../../../services/supplierService";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { handleErrors } from "../../../utils/errorHandler";
import ComponentHeader from "../../Common/OtherElements/ComponentHeader";
import { InputField } from "../../Common/FormElements/InputField";
import { useFormValidation } from "../../../hooks/useFormValidation";
import { usePageLevelAccess } from "../../../hooks/usePageLevelAccess";


export const AddSupplier = ({
  editMode = false,
  initialData = {},
  onSuccess,
  setSelectedSupplier,
  setEditMode,
}) => {
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);

  const navigate = useNavigate();
  const PageLevelAccessurl = "supplier-master";
  const { pageAccessData } = usePageLevelAccess(PageLevelAccessurl);
  // ✅ Form validation hook
  const {
    formErrors,
    isFormValid,
    registerField,
    validateAllFields,
    clearErrors,
    touchAllFields,
    getFieldLabel,
  } = useFormValidation();

  // ✅ Create refs for each field
  const nameRef = useRef(null);
  const emailRef = useRef(null);
  const contactNoRef=useRef(null);
  const companyNameRef=useRef(null);
  const gstNoRef=useRef(null);
  const addressRef=useRef(null);
  const locationRef=useRef(null);


  // ✅ Initialize all state properly
  const [formValues, setFormValues] = useState({
    name: "",
    email: "",
    contactNo:"",
    companyName:"",
    gstNo:"",
    address:"",
    location:"",

  });


  useEffect(() => {
    registerField("name", nameRef, "Name");
    registerField("email", emailRef, "Email");
    registerField("contactNo",contactNoRef,"Contact Number");
    registerField("companyName",companyNameRef,"Company Name");
    registerField("gstNo",gstNoRef,"GST Number");
    registerField("location",locationRef,"Location");

  }, [registerField]);

  // Add this useEffect to validate after formValues change
  useEffect(() => {
    // Only validate if form has been touched/submitted before
    if (Object.keys(formErrors).length > 0) {
      const timeoutId = setTimeout(() => {
        validateAllFields();
      }, 100); // Small delay to ensure refs are updated

      return () => clearTimeout(timeoutId);
    }
  }, [formValues, validateAllFields, formErrors]);

  // ✅ Fix page access check with proper dependencies
  useEffect(() => {
    if (pageAccessData) {
      if (!pageAccessData.addAccess || !pageAccessData.viewAccess) {
        navigate("/404-error-page");
      }
    }
  }, [pageAccessData, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      if (editMode && initialData.id) {
        try {
          const data = await fetchSupplierbyId(initialData.id);
          if (data && data.isSuccess && data.result) {
            setFormValues({
              name: data.result.name,
              email: data.result.email,
              contactNo:data.result.contactNo,
              companyName:data.result.companyName,
              gstNo:data.result.gstNo,
              address:data.result.address,
              location:data.result.location
            });
          }
        } catch (error) {
          handleErrors(error);
        }
      } else {
        setFormValues({ name: "", email: "",contactNo:"",companyName:"" ,gstNo:"",address:"",location:""});
      }
    };
    fetchData();
  }, [editMode, initialData]);

  const handleInputChange = (e) => {
    const { id, type, value, checked } = e.target;
     const inputValue = type === "checkbox" ? checked : value;

    setFormValues((prev) => {
      const newValues = {
        ...prev,
        [id]: inputValue,
      };
      return newValues;
    });
  };

  // ✅ Reset all form fields
  const resetAllFields = () => {
    const fieldRefs = {
      nameRef,
      emailRef,
      contactNoRef,
      companyNameRef,
      gstNoRef,
      addressRef,
      locationRef
    };
    Object.values(fieldRefs).forEach((ref) => {
      if (ref.current && ref.current.reset) {
        ref.current.reset();
      }
    });
  };

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      // ✅ Touch all fields to show validation errors
      touchAllFields();

      // ✅ Validate all fields using refs
      const isValid = validateAllFields();

      if (!isValid) {
        toast.error("Please fix all validation errors before submitting.");
        return;
      }

      try {
        if (editMode) {
          setIsButtonDisabled(true);
          var updateResponseData = await updateSupplier({
            ...formValues,
            id: initialData.id,
          });
          // ✅ Clear form errors
          clearErrors();
          if (updateResponseData?.data.isSuccess) {
            toast.success(updateResponseData?.data.statusMessage);
          } else {
            toast.error(updateResponseData?.data.statusMessage);
          }
        } else {
          setIsButtonDisabled(true);
          var createResponseData = await createSupplier(formValues);
          resetAllFields();
          setFormValues({
            name: "",
            email: "",
            contactNo:"",
            companyName:"",
            gstNo:"",
            address:"",
            location:"",

          });
          // ✅ Clear form errors
          clearErrors();
          if (createResponseData?.data.isSuccess) {
            toast.success(createResponseData?.data.statusMessage);
          } else {
            toast.error(createResponseData?.data.statusMessage);
          }
        }
        if (onSuccess) onSuccess();
      } catch (error) {
        handleErrors(error);
      } finally {
        setIsButtonDisabled(false);
      }
    },
    [formValues, editMode, initialData, onSuccess, setEditMode]
  );

  const handleAddNewClick = () => {
    setFormValues({
       name: "",
      email: "",
      contactNo:"",
      companyName:"",
      gstNo:"",
      address:"",
      location:"",
    });
    resetAllFields();
    setSelectedSupplier(null);
    setEditMode(false);
  };

  return (
    <>
      <ComponentHeader title="Supplier" />
      {Object.keys(formErrors).length > 0 && (
        <div className="alert alert-warning mb-3">
          <strong>Please fix the following errors:</strong>
          <ul className="mb-0 mt-2">
            {Object.entries(formErrors).map(([fieldId, error]) => (
              <li key={fieldId}>
                <strong>{getFieldLabel(fieldId)}:</strong> {error}
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="row">
        <div className="col-xxl-12">
          <div className="card mt-xxl-n5">
            <div className="card-header">
              <h5 className="mb-sm-1 mt-sm-1">
                {editMode ? "Update Supplier" : "Add Supplier"}
              </h5>
            </div>

            <div className="card-body p-4">
              <form onSubmit={handleSubmit} method="POST">
                <div className="row">
                  <div className="col-lg-3 col-md-6 col-sm-12">
                    <InputField
                      ref={nameRef}
                      label="Name"
                      id="name"
                      value={formValues.name}
                      placeholder="Enter your Name"
                      onChange={handleInputChange}
                      required={true}
                      showError={true}
                    />
                  </div>

                  <div className="col-lg-3 col-md-6 col-sm-12">
                    <InputField
                      ref={emailRef}
                      label="Email"
                      id="email"
                      value={formValues.email}
                      placeholder="Enter your Email"
                      onChange={handleInputChange}
                      required={true}
                      validationType="email"                     
                      
                      showError={true}
                    />
                  </div>
                   <div className="col-lg-3 col-md-6 col-sm-12">
                    <InputField
                      ref={contactNoRef}
                      label="Contact Number"
                      id="contactNo"
                      value={formValues.contactNo}
                      placeholder="Enter your Contact Number"
                      onChange={handleInputChange}
                      required={true}
                      validationType="phone"
                   
                      showError={true}
                    />
                  </div> 
                   

                   <div className="col-lg-3 col-md-6 col-sm-12">
                    <InputField
                      ref={companyNameRef}
                      label="Company Name"
                      id="companyName"
                      value={formValues.companyName}
                      placeholder="Enter your Company Name"
                      onChange={handleInputChange}
                      required={true}
                      showError={true}
                    />
                  </div>
                  <div className="col-lg-3 col-md-6 col-sm-12">
                    <InputField
                      ref={gstNoRef}
                      label="GST Number"
                      id="gstNo"
                      value={formValues.gstNo}
                      placeholder="Enter your GST Number"
                      onChange={handleInputChange}
                      validationType="alphanumeric"
                      required={true}
                      showError={true}
                    />
                  </div>
                  <div className="col-lg-3 col-md-6 col-sm-12">
                    <InputField
                      ref={addressRef}
                      label="Address"
                      id="address"
                      value={formValues.address}
                      placeholder="Enter your Address"
                      onChange={handleInputChange}
                      
                      required={true}
                      showError={true}
                    />
                  </div>
                  <div className="col-lg-3 col-md-6 col-sm-12">
                    <InputField
                      ref={locationRef}
                      label="Location"
                      id="location"
                      value={formValues.location}
                      placeholder="Enter your Location"
                      onChange={handleInputChange}
                      
                      required={true}
                      showError={true}
                    />
                  </div>
                  <div className="col-lg-12">
                    <div>
                      <button
                        type="submit"
                        className="btn btn-secondary pt-1 pb-1 p-3"
                        disabled={isButtonDisabled || !isFormValid}
                      >
                        {isButtonDisabled
                          ? editMode
                            ? "Updating"
                            : "Saving"
                          : editMode
                          ? "Update"
                          : "Save"}
                      </button>
                      {editMode && (
                        <button
                          type="button"
                          onClick={handleAddNewClick}
                          className="btn btn-danger ms-1 pt-1 pb-1 p-3"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
